import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../../data";
import { useStaffWorkspace } from "../../../app/session";
import { talentName, useBrands, useDeals, useTalents } from "../../../data/queries";
import { Dialog, useAction } from "../../../components/ui";
import { today as todayIn } from "../../../lib/format";
import { registerExternal, type KindRow } from "./daten";
import { KooperationAuswahl } from "./KooperationAuswahl";
import { istCreatorKonto, t, nameIn } from "../../../lib/sprache";

const MAX_BYTES = 15 * 1024 * 1024;

/**
 * Extern (auf Papier oder in einem anderen Werkzeug) unterschriebenen Vertrag als PDF ablegen (fs207, TA-100, Befund 14):
 * mit Creator oder nur mit einer Marke (etwa Geheimhaltung, Rahmenvertrag mit der Marke). Erst mit dem PDF gilt er als
 * abgeschlossen; externe Verträge zählen nicht gegen das Monatskontingent des Tarifs.
 */
export function ExternerVertragDialog({ onClose, talentId: presetTalent, dealId: presetDeal }: { onClose: () => void; talentId?: string; dealId?: string }) {
  const ws = useStaffWorkspace();
  const orgId = ws.org.org_id;
  const navigate = useNavigate();
  const talents = useTalents(orgId);
  const brands = useBrands(orgId);
  const deals = useDeals(orgId);
  const kinds = useQuery({ queryKey: ["contract_kinds", orgId], queryFn: () => api.select<KindRow>("contract_kinds", { filters: [["org_id", "eq", orgId]], order: [["sort_order", "asc"]] }) });
  const presetDealRow = deals.data?.find((d) => d.id === presetDeal);
  const [talentId, setTalentId] = useState(presetTalent ?? presetDealRow?.talent_id ?? "");
  const [title, setTitle] = useState("");
  const [signedOn, setSignedOn] = useState(todayIn(ws.org.timezone));
  const [language, setLanguage] = useState<"de" | "en">("de");
  const [brandId, setBrandId] = useState(presetDealRow?.brand_id ?? "");
  const [dealId, setDealId] = useState(presetDeal ?? "");
  const [kindId, setKindId] = useState("");
  // Befund 7: verborgen, bis die Agentur es ausdrücklich freigibt (oder eine Fassung für den Creator ablegt)
  const [visible, setVisible] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [talentFile, setTalentFile] = useState<File | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const { busy, error, run } = useAction();

  const checkPdf = (f: File | null) => (!f ? null : f.type !== "application/pdf" ? t("Bitte eine PDF-Datei wählen.", "Please choose a PDF file.") : f.size > MAX_BYTES ? t("Die Datei ist größer als 15 MB.", "The file is larger than 15 MB.") : null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const p = !talentId && !brandId ? t("Bitte einen Creator oder eine Marke wählen.", "Please choose a creator or a brand.") : title.trim().length < 3 ? t("Bitte einen Titel angeben.", "Please enter a title.")
      : !file ? t("Bitte das unterschriebene PDF wählen.", "Please choose the signed PDF.") : checkPdf(file) ?? checkPdf(talentFile);
    setProblem(p);
    if (p || !file) return;
    const id = await run("extern", () => registerExternal(api, orgId, {
      talentId: talentId || null, title: title.trim(), signedOn, language, brandId: brandId || null, dealId: dealId || null, kindId: kindId || null,
      file, fileName: file.name, talentFile, visibleToTalent: talentId ? visible : null,
    }), talentId && visible ? (talentFile ? t("Vertrag abgelegt. Der Creator sieht die Fassung für den Creator in seinem Portal.", "Contract filed. The creator sees the creator version in their portal.") : t("Vertrag abgelegt. Der Creator sieht das vollständige PDF in seinem Portal.", "Contract filed. The creator sees the complete PDF in their portal.")) : talentId ? t("Vertrag abgelegt. Der Creator sieht ihn nicht.", "Contract filed. The creator does not see it.") : t("Vertrag abgelegt.", "Contract filed."));
    if (id) {
      onClose();
      navigate(`/agentur/vertraege/${id}`);
    }
  }

  return (
    <Dialog open onClose={onClose} title={t("Extern unterschriebenen Vertrag erfassen", "Record externally signed contract")}>
      <form className="stack" onSubmit={(e) => void submit(e)} noValidate aria-busy={!!busy}>
        <p>{t("Für Verträge, die außerhalb des Portals unterschrieben wurden. Mit dem PDF gilt der Vertrag als abgeschlossen; er zählt nicht als Versand im Tarif.", "For contracts signed outside the portal. With the PDF the contract counts as concluded; it does not count as a dispatch in the plan.")}</p>
        <div className="grid">
          <label>
            {t("Creator (optional)", "Creator (optional)")}<select value={talentId} onChange={(e) => { setTalentId(e.target.value); setDealId(""); }}>
              <option value="">{t("Ohne Creator (z. B. Vertrag nur mit der Marke)", "Without creator (e.g. contract with the brand only)")}</option>
              {(talents.data ?? []).filter((t) => t.status !== "archived").map((t) => <option key={t.id} value={t.id}>{talentName(t)}</option>)}
            </select>
          </label>
          <label>
            {t("Marke", "Brand")}{talentId ? " (optional)" : ""}
            <select value={brandId} onChange={(e) => { setBrandId(e.target.value); if (dealId && deals.data?.find((x) => x.id === dealId)?.brand_id !== e.target.value) setDealId(""); }}>
              <option value="">{t("Ohne Marke", "Without brand")}</option>
              {(brands.data ?? []).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </label>
        </div>
        <label>
          {t("Titel", "Title")}<input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("z. B. Rahmenvertrag 2026", "e.g. framework contract 2026")} required />
        </label>
        <div className="grid">
          <label>
            {t("Unterschrieben am", "Signed on")}<input type="date" value={signedOn} max={todayIn(ws.org.timezone)} onChange={(e) => setSignedOn(e.target.value)} required />
          </label>
          <label>
            {t("Sprache", "Language")}<select value={language} onChange={(e) => setLanguage(e.target.value as "de" | "en")}>
              <option value="de">{t("Deutsch", "German")}</option>
              <option value="en">{t("Englisch", "English")}</option>
            </select>
          </label>
        </div>
        <KooperationAuswahl label={t("Kooperation (optional)", "Deal (optional)")} value={dealId} onChange={(id) => { setDealId(id); const d = deals.data?.find((x) => x.id === id); if (d) { setBrandId(d.brand_id); setTalentId(d.talent_id); } }}
          deals={deals.data ?? []} brands={brands.data ?? []} talents={talents.data ?? []} talentId={talentId || null} brandId={brandId || null} showClosed />
        <label>
          {t("Vertragsart (optional)", "Contract type (optional)")}<select value={kindId} onChange={(e) => setKindId(e.target.value)}>
            <option value="">{t("Ohne Vertragsart", "No contract type")}</option>
            {(kinds.data ?? []).filter((k) => !k.archived_at).map((k) => <option key={k.id} value={k.id}>{nameIn(k.name_de, k.name_en)}</option>)}
          </select>
        </label>
        <label>
          {t("Unterschriebenes PDF", "Signed PDF")}<input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} required />
        </label>
        {/* Studio (Creator-Konto): kein Creator-Portal, keine Freigabe und keine Fassung für den Creator */}
        {talentId && !istCreatorKonto() && (
          <>
            <label className="row"><input type="checkbox" checked={visible} onChange={(e) => setVisible(e.target.checked)} />{t("Für den Creator freigeben (er sieht den Vertrag in seinem Portal)", "Release to the creator (they see the contract in their portal)")}</label>
            <label>
              {t("Fassung für den Creator ohne vertraulichen Teil (optional)", "Creator version without the confidential part (optional)")}<input type="file" accept="application/pdf" onChange={(e) => { const f = e.target.files?.[0] ?? null; setTalentFile(f); if (f) setVisible(true); }} />
              <small>{t("Mit dieser Fassung sieht der Creator nur sie, nie das vollständige PDF (etwa ohne die Konditionen der Agentur).", "With this version the creator only sees it, never the complete PDF (e.g. without the agency's terms).")}</small>
            </label>
            {visible && !talentFile && <small className="tk-warning">{t("Ohne Fassung für den Creator sieht er das vollständige PDF, auch Konditionen der Agentur.", "Without a creator version they see the complete PDF, including the agency's terms.")}</small>}
            {!visible && <small>{t("Der Creator sieht diesen Vertrag nicht. Freigeben lässt er sich später im Vertrag.", "The creator does not see this contract. It can be released later in the contract.")}</small>}
          </>
        )}
        {(problem || error) && <p className="tk-error" role="alert">{problem ?? error}</p>}
        <div className="row">
          <button className="primary" type="submit" disabled={!!busy}>{busy ? t("Wird abgelegt …", "Filing …") : t("Vertrag ablegen", "File contract")}</button>
          <button type="button" onClick={onClose}>{t("Abbrechen", "Cancel")}</button>
        </div>
      </form>
    </Dialog>
  );
}
